// @ts-check
/**
 * D2C bill-of-materials — pick a product, pick a size+color variant, record what it
 * actually takes to construct (fabric, trims) and where each was sourced from.
 * Standalone page (not embedded in ProductEditModal) so the modal doesn't bloat.
 */

import { ArrowLeft, Copy, Edit2, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import adminMaterialService from '../services/adminMaterialService';
import { adminProductVariantService } from '../services/adminProductExtrasService';
import adminProductMaterialService from '../services/adminProductMaterialService';
import adminProductService from '../services/adminProductService';

const PROCESSES = [
  { value: '', label: 'Use material default' },
  { value: 'raw', label: 'Raw' },
  { value: 'printed', label: 'Printed' },
  { value: 'dyed', label: 'Dyed' },
  { value: 'embroidered', label: 'Embroidered' },
  { value: 'hand_painted', label: 'Hand-painted' },
  { value: 'other', label: 'Other' },
];

const EMPTY_LINE = {
  material_id: '',
  process: '',
  quantity_per_unit: '',
  wastage_percentage: '0',
  unit_cost: '',
  currency: 'INR',
  exchange_rate: '1',
  customs_duty_percentage: '0',
  freight_cost_per_unit: '0',
  color_or_variant_note: '',
  lot_number: '',
  notes: '',
};

function variantLabel(v) {
  return [v.size, v.color].filter(Boolean).join(' / ') || v.sku || v.id.slice(0, 8);
}

export default function ProductMaterialsPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLineId, setEditingLineId] = useState(null);
  // Touched-since-open flag, reset by openCreate/openEdit (loading a form
  // never itself counts as an edit) and after a successful save.
  const [isDirty, setIsDirty] = useState(false);
  const [form, setFormRaw] = useState(EMPTY_LINE);
  const setForm = useCallback((updater) => {
    setIsDirty(true);
    setFormRaw(updater);
  }, []);
  const [saving, setSaving] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [copySourceId, setCopySourceId] = useState('');

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [p, v, m] = await Promise.all([
        adminProductService.getProduct(productId),
        adminProductVariantService.getProductVariants(productId),
        adminMaterialService.getMaterials(),
      ]);
      setProduct(p);
      setVariants(v || []);
      setMaterials(m || []);
      if (v?.length && !selectedVariantId) setSelectedVariantId(v[0].id);
    } catch (err) {
      toast.error(err.message || 'Failed to load product');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const loadVariantMaterials = useCallback(async () => {
    if (!selectedVariantId) return;
    try {
      const [lineData, summaryData] = await Promise.all([
        adminProductMaterialService.getVariantMaterials(productId, selectedVariantId),
        adminProductMaterialService.getVariantMaterialsSummary(productId, selectedVariantId),
      ]);
      setLines(lineData || []);
      setSummary(summaryData);
    } catch (err) {
      toast.error(err.message || 'Failed to load material lines');
    }
  }, [productId, selectedVariantId, toast]);

  useEffect(() => {
    loadBase();
  }, [loadBase]);
  useEffect(() => {
    loadVariantMaterials();
  }, [loadVariantMaterials]);

  const openCreate = () => {
    setEditingLineId(null);
    setFormRaw(EMPTY_LINE);
    setIsDirty(false);
    setShowModal(true);
  };

  const openEdit = (line) => {
    setEditingLineId(line.id);
    setFormRaw({
      material_id: line.material_id,
      process: line.process || '',
      quantity_per_unit: String(line.quantity_per_unit),
      wastage_percentage: String(line.wastage_percentage),
      unit_cost: String(line.unit_cost),
      currency: line.currency,
      exchange_rate: String(line.exchange_rate),
      customs_duty_percentage: String(line.customs_duty_percentage),
      freight_cost_per_unit: String(line.freight_cost_per_unit),
      color_or_variant_note: line.color_or_variant_note || '',
      lot_number: line.lot_number || '',
      notes: line.notes || '',
    });
    // The material may have since been removed from the active catalog — keep it
    // selectable in this dropdown so editing the line doesn't silently blank it out.
    if (line.material_id && !materials.some((m) => m.id === line.material_id)) {
      setMaterials((prev) => [
        ...prev,
        { id: line.material_id, name: line.material_name || 'Deleted material', category: line.material_category },
      ]);
    }
    setIsDirty(false);
    setShowModal(true);
  };

  // X/Cancel dismiss the modal — confirm first if there's anything to lose.
  // A successful save closes directly (see handleSave), bypassing this.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setShowModal(false);
  };

  const handleMaterialChange = async (materialId) => {
    setForm((prev) => ({ ...prev, material_id: materialId }));
    if (!materialId || editingLineId) return;
    try {
      const prefill = await adminMaterialService.getMaterialPrefill(materialId);
      setForm((prev) => ({
        ...prev,
        process: prefill.process || prev.process,
        unit_cost: prefill.unit_cost != null ? String(prefill.unit_cost) : prev.unit_cost,
        currency: prefill.currency || prev.currency,
        exchange_rate: prefill.exchange_rate != null ? String(prefill.exchange_rate) : prev.exchange_rate,
        customs_duty_percentage:
          prefill.customs_duty_percentage != null
            ? String(prefill.customs_duty_percentage)
            : prev.customs_duty_percentage,
        freight_cost_per_unit:
          prefill.freight_cost_per_unit != null ? String(prefill.freight_cost_per_unit) : prev.freight_cost_per_unit,
      }));
    } catch {
      // pre-fill is a convenience only — silently skip if it fails
    }
  };

  const handleSave = async () => {
    if (!form.material_id || !form.quantity_per_unit || form.unit_cost === '') {
      toast.error('Material, quantity, and unit cost are required');
      return;
    }
    const payload = {
      material_id: form.material_id,
      process: form.process || null,
      quantity_per_unit: Number(form.quantity_per_unit),
      wastage_percentage: Number(form.wastage_percentage || 0),
      unit_cost: Number(form.unit_cost),
      currency: form.currency,
      exchange_rate: Number(form.exchange_rate || 1),
      customs_duty_percentage: Number(form.customs_duty_percentage || 0),
      freight_cost_per_unit: Number(form.freight_cost_per_unit || 0),
      color_or_variant_note: form.color_or_variant_note.trim() || null,
      lot_number: form.lot_number.trim() || null,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      if (editingLineId) {
        await adminProductMaterialService.updateVariantMaterial(productId, selectedVariantId, editingLineId, payload);
        toast.success('Material line updated');
      } else {
        await adminProductMaterialService.addVariantMaterial(productId, selectedVariantId, payload);
        toast.success('Material line added');
      }
      setIsDirty(false);
      setShowModal(false);
      await loadVariantMaterials();
    } catch (err) {
      toast.error(err.message || 'Failed to save material line');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (line) => {
    if (!window.confirm('Delete this material line? This cannot be undone.')) return;
    try {
      await adminProductMaterialService.deleteVariantMaterial(productId, selectedVariantId, line.id);
      toast.success('Material line removed');
      await loadVariantMaterials();
    } catch (err) {
      toast.error(err.message || 'Failed to delete material line');
    }
  };

  const handleCopy = async () => {
    if (!copySourceId) return;
    try {
      await adminProductMaterialService.copyFromVariant(productId, selectedVariantId, copySourceId);
      toast.success('Materials copied — adjust fabric quantities for this size');
      setShowCopy(false);
      setCopySourceId('');
      await loadVariantMaterials();
    } catch (err) {
      toast.error(err.message || 'Failed to copy materials');
    }
  };

  const materialName = (id) => materials.find((m) => m.id === id)?.name || '—';

  if (loading) return <LoadingScreen />;

  const selectedVariant = variants.find((v) => v.id === selectedVariantId);

  return (
    <div>
      <button
        onClick={() => navigate('/products')}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-3 hover:text-foreground"
      >
        <ArrowLeft size={16} /> Back to products
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-serif">Materials — {product?.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            What this variant actually takes to construct, and where each material was sourced from.
          </p>
        </div>
      </div>

      {variants.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border rounded-lg border-border">
          This product has no variants yet — add sizes/colors first.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm font-medium">Variant</label>
            <select
              value={selectedVariantId}
              onChange={(e) => setSelectedVariantId(e.target.value)}
              className="px-3 py-2 border rounded-lg border-input text-sm"
            >
              {variants.map((v) => (
                <option key={v.id} value={v.id}>
                  {variantLabel(v)}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowCopy(true)}
              className="px-3 py-2 text-sm border rounded-lg border-input hover:bg-accent flex items-center gap-1"
            >
              <Copy size={14} /> Copy from another variant
            </button>
            <button
              onClick={openCreate}
              className="ml-auto px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 text-sm"
            >
              <Plus size={16} /> Add Material Line
            </button>
          </div>

          {summary && (
            <div className="bg-muted/50 border rounded-lg border-border px-4 py-3 mb-4 text-sm">
              Total landed material cost for {selectedVariant ? variantLabel(selectedVariant) : 'this variant'}:{' '}
              <span className="font-medium">₹{summary.total_cost_per_unit_inr?.toFixed(2)}</span> per unit
            </div>
          )}

          {lines.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground bg-card border rounded-lg border-border">
              No material lines for this variant yet.
            </div>
          ) : (
            <div className="bg-card border rounded-lg border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Material</th>
                    <th className="text-left px-4 py-3 font-medium">Qty / Unit</th>
                    <th className="text-left px-4 py-3 font-medium">Wastage</th>
                    <th className="text-left px-4 py-3 font-medium">Unit Cost</th>
                    <th className="text-left px-4 py-3 font-medium">Landed Cost</th>
                    <th className="text-left px-4 py-3 font-medium">Sourced From</th>
                    <th className="text-left px-4 py-3 font-medium">Lot #</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.id} className="border-b border-border/60 last:border-b-0">
                      <td className="px-4 py-3">
                        <p className="font-medium">
                          {line.material_name || materialName(line.material_id)}
                          {line.process && (
                            <span className="text-xs text-muted-foreground/70 font-normal capitalize">
                              {' '}
                              — {line.process.replace(/_/g, ' ')}
                            </span>
                          )}
                        </p>
                        {line.color_or_variant_note && (
                          <p className="text-xs text-muted-foreground/70">{line.color_or_variant_note}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">{line.quantity_per_unit}</td>
                      <td className="px-4 py-3">{line.wastage_percentage}%</td>
                      <td className="px-4 py-3">
                        {line.currency} {line.unit_cost}
                      </td>
                      <td className="px-4 py-3">₹{line.landed_unit_cost_inr?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{line.vendor_name || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{line.lot_number || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(line)}
                            className="p-2 text-muted-foreground hover:text-foreground"
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(line)}
                            className="p-2 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showCopy && (
        <Modal
          maxWidth="md"
          fullHeight={false}
          onClose={() => setShowCopy(false)}
          className="p-6"
          closeOnBackdrop={false}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-serif">Copy materials from another variant</h2>
            <button onClick={() => setShowCopy(false)} className="text-muted-foreground hover:text-foreground">
              <X size={20} />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Clones every material line — trims and lining usually stay the same; adjust fabric quantity for the new size
            afterward.
          </p>
          <select
            value={copySourceId}
            onChange={(e) => setCopySourceId(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg border-input mb-4"
          >
            <option value="">Select source variant…</option>
            {variants
              .filter((v) => v.id !== selectedVariantId)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {variantLabel(v)}
                </option>
              ))}
          </select>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowCopy(false)}
              className="px-4 py-2 border rounded-lg border-input hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={handleCopy}
              disabled={!copySourceId}
              className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50"
            >
              Copy
            </button>
          </div>
        </Modal>
      )}

      {showModal && (
        <Modal maxWidth="xl" onClose={handleCloseClick} closeOnBackdrop={false} hasUnsavedChanges={isDirty}>
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-serif">{editingLineId ? 'Edit Material Line' : 'Add Material Line'}</h2>
            <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
              <X size={22} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Material *</label>
              <select
                value={form.material_id}
                onChange={(e) => handleMaterialChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
              >
                <option value="">Select material…</option>
                {materials.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Process</label>
              <select
                value={form.process}
                onChange={(e) => setForm({ ...form, process: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
              >
                {PROCESSES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Tags what this specific line was used for — e.g. this fabric printed vs. the same fabric dyed on another
                line.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Quantity per Unit *</label>
                <input
                  type="number"
                  step="0.0001"
                  value={form.quantity_per_unit}
                  onChange={(e) => setForm({ ...form, quantity_per_unit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Wastage %</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.wastage_percentage}
                  onChange={(e) => setForm({ ...form, wastage_percentage: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Unit Cost *</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.unit_cost}
                  onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Currency</label>
                <input
                  type="text"
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Exchange Rate → INR</label>
                <input
                  type="number"
                  step="0.000001"
                  value={form.exchange_rate}
                  onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Customs Duty %</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.customs_duty_percentage}
                  onChange={(e) => setForm({ ...form, customs_duty_percentage: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Freight Cost / Unit (INR)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.freight_cost_per_unit}
                  onChange={(e) => setForm({ ...form, freight_cost_per_unit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Color / Variant Note</label>
                <input
                  type="text"
                  value={form.color_or_variant_note}
                  onChange={(e) => setForm({ ...form, color_or_variant_note: e.target.value })}
                  placeholder="e.g. dyed to match"
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Lot / Batch Number</label>
                <input
                  type="text"
                  value={form.lot_number}
                  onChange={(e) => setForm({ ...form, lot_number: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex justify-end gap-2">
            <button
              onClick={handleCloseClick}
              disabled={saving}
              className="px-4 py-2 border rounded-lg border-input hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50"
            >
              {saving ? 'Saving...' : 'Save Line'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
